import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, RefreshCw, PlayCircle, Lock } from "lucide-react";
import { ListingHeader } from "../../../components/listing-header";
import CourseViewer from "../../../components/designs/course-viewer";
import Loader from "../../../components/designs/loader";
import Modal from "../../../components/designs/modal";
import axiosService from "../../../core/services/axios-service";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import { COURSE_REGISTRY } from "../../../core/dictionary/course-registry";

// ── Circular progress ring (lives in the ListingHeader rightSlot) ─────────────
function ProgressRing({ progress, status, saving }) {
  const clamped = Math.min(100, Math.max(0, progress));
  const isCompleted = status === "completed" || status === "passed" || clamped >= 100;
  const ringOffset = 100 - clamped;

  return (
    <div className="course-progress-ring">
      {saving && (
        <span className="course-progress-ring__status course-progress-ring__status--saving">
          <RefreshCw size={12} className="course-progress-ring__spin" />
          Saving…
        </span>
      )}
      {!saving && isCompleted && (
        <span className="course-progress-ring__status course-progress-ring__status--done">
          <CheckCircle size={13} />
          Completed
        </span>
      )}
      <div className="course-progress-ring__container">
        <svg viewBox="0 0 36 36" className="course-progress-ring__svg">
          <circle className="course-progress-ring__bg" cx="18" cy="18" r="15.9155" />
          <circle
            className={`course-progress-ring__fill${isCompleted ? " course-progress-ring__fill--done" : ""}`}
            cx="18" cy="18" r="15.9155"
            style={{ "--ring-offset": ringOffset }}
          />
        </svg>
        <span className="course-progress-ring__label">{clamped}%</span>
      </div>
    </div>
  );
}

// ── Confetti pieces for the completion celebration ───────────────────────────
// Pre-computed once with stable, well-spread positions, sizes, colours, and a
// per-piece horizontal drift (--drift) + rotation (--rotate) used by the CSS.
const CONGRATS_COLORS = ["#ef4444", "#f59e0b", "#fbbf24", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899"];
const CONFETTI_PIECES = Array.from({ length: 70 }, (_, i) => {
  const size = 7 + (i % 4) * 3;
  return {
    left: (i * 100) / 70,
    color: CONGRATS_COLORS[i % CONGRATS_COLORS.length],
    width: size,
    height: size * 1.6,
    delay: (i % 14) * 0.22,
    duration: 3 + (i % 6) * 0.4,
    drift: (i % 2 === 0 ? 1 : -1) * (30 + (i % 6) * 18),
    rotate: 540 + (i % 5) * 180,
  };
});

// ─────────────────────────────────────────────────────────────────────────────

export default function CoachTeamMemberCourse() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const course = COURSE_REGISTRY[id];
  const modules = useMemo(() => course?.modules ?? [], [course]);

  // Module to open on entry — passed from the dashboard based on coursePercentage
  // (floor(progress / 20)). Clamped to the available module range.
  const startModuleIndex = Math.min(
    Math.max(0, location.state?.startModuleIndex ?? 0),
    Math.max(0, modules.length - 1)
  );

  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(
    modules[startModuleIndex]?.slug ?? modules[0]?.slug ?? null
  );
  // progressMap: moduleId → { percentage, status }
  const [progressMap, setProgressMap] = useState({});
  const [currentProgress, setCurrentProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState("not attempted");
  const [saving, setSaving] = useState(false);
  // Manual module-completion flow (Mark as Completed → Start Next Module).
  const [markingComplete, setMarkingComplete] = useState(false);
  const [startingNext, setStartingNext] = useState(false);
  const [showNextConfirm, setShowNextConfirm] = useState(false);
  const [actionError, setActionError] = useState("");
  // Course-completion celebration modal + 15s auto-redirect countdown.
  const [showCongrats, setShowCongrats] = useState(false);
  const [countdown, setCountdown] = useState(15);
  // Guards the "all modules completed" celebration so it only fires once.
  const allCompleteAlerted = useRef(false);
  // Always-current mirror of progressMap — lets handleProgressChange read
  // the latest map without putting progressMap in its deps.
  const progressMapRef = useRef({});
  progressMapRef.current = progressMap;

  // ── Fetch all module progress on mount ────────────────────────────────────
  useEffect(() => {
    if (!course) { setLoading(false); return; }
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axiosService.get(ApiRoutes.ASSIGNED_COURSES_BY_USER);
        const assigned = res?.data?.data ?? [];

        // The backend stores ONE overall coursePercentage per course — not a
        // separate entry per module. Derive each module's state from it, where
        // every 20% represents one completed module:
        //   index < completedCount → completed (unlocks the next module)
        //   index === completedCount → current module (carries partial %)
        //   index > completedCount → not started (locked)
        const overall =
          assigned.find((item) => item.course._id === id)?.coursePercentage ?? 0;
        const completedCount = Math.floor(overall / 20);

        const map = {};
        modules.forEach((mod, idx) => {
          if (idx < completedCount) {
            map[mod.moduleId] = { percentage: 100, status: "completed" };
          } else if (idx === completedCount) {
            const partial = Math.max(
              0,
              Math.min(100, ((overall - completedCount * 20) / 20) * 100)
            );
            map[mod.moduleId] = {
              percentage: partial,
              status: partial > 0 ? "incomplete" : "not attempted",
            };
          } else {
            map[mod.moduleId] = { percentage: 0, status: "not attempted" };
          }
        });
        setProgressMap(map);
        // Seed the active module's current progress
        const startMod = modules[startModuleIndex] ?? modules[0];
        if (startMod) {
          setCurrentProgress(map[startMod.moduleId]?.percentage ?? 0);
          setCurrentStatus(map[startMod.moduleId]?.status ?? "not attempted");
        }
      } catch {
        // Fall through with zero progress
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync current progress when active module changes ─────────────────────
  useEffect(() => {
    if (!activeSlug) return;
    const mod = modules.find((m) => m.slug === activeSlug);
    if (!mod) return;
    setCurrentProgress(progressMap[mod.moduleId]?.percentage ?? 0);
    setCurrentStatus(progressMap[mod.moduleId]?.status ?? "not attempted");
    setSaving(false);
  }, [activeSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Module completion: alert when the whole course is finished ─────────────
  //   Advancing to the next module is now an explicit user action
  //   (Mark as Completed → Start Next Module), so there is no timed auto-advance.
  useEffect(() => {
    const isDone = currentStatus === "completed" || currentStatus === "passed" || currentProgress >= 100;
    if (!isDone) return;

    const currentIndex = modules.findIndex((m) => m.slug === activeSlug);
    if (currentIndex < 0) return;

    const completedMod = modules[currentIndex];
    // ── Last module completed → check the whole course is done ───────────────
    const everyModuleDone = modules.every((m) => {
      const entry = m.slug === completedMod.slug
        ? { status: currentStatus, percentage: currentProgress }
        : (progressMapRef.current[m.moduleId] ?? {});
      return (
        entry.status === "completed" ||
        entry.status === "passed" ||
        (entry.percentage ?? 0) >= 100
      );
    });

    if (everyModuleDone && !allCompleteAlerted.current) {
      allCompleteAlerted.current = true;
      setCountdown(15);
      setShowCongrats(true);
    }
  }, [currentStatus, currentProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Course-complete celebration: tick down and auto-redirect after 15s ─────
  useEffect(() => {
    if (!showCongrats) return;
    if (countdown <= 0) {
      navigate("/dashboard");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showCongrats, countdown, navigate]);

  // ── Save overall course progress to new API (silent, no toaster) ─────────
  //
  // coursePercentage accounts for BOTH completed modules AND partial progress
  // in any in-progress module:
  //   completed  → full perPct (20 for a 5-module course)
  //   in-progress → proportional fraction  e.g. 50 % of module = 10
  //   not started → 0
  //
  // Using only completedCount * perPct would always send 0 until an entire
  // module finishes, which means slide-level progress is never reflected.
  const saveOverallProgress = useCallback(async (map, currentModuleIndex) => {
    const perPct = modules.length > 0 ? Math.floor(100 / modules.length) : 20;

    let coursePercentage = 0;
    modules.forEach((m) => {
      const entry = map[m.moduleId];
      if (!entry) return;
      const isComplete =
        entry.status === "completed" ||
        entry.status === "passed" ||
        (entry.percentage ?? 0) >= 100;
      coursePercentage += isComplete
        ? perPct
        : Math.floor(((entry.percentage ?? 0) * perPct) / 100);
    });
    coursePercentage = Math.min(100, coursePercentage);

    try {
      await axiosService.post(ApiRoutes.COURSE_PROGRESS, {
        courseId: id,
        coursePercentage,
        currentPage: String(currentModuleIndex + 1),
      });
    } catch {
      // Silent — no toaster for progress saves
    }
  }, [id, modules]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Returns true if the module at index is not yet unlocked ──────────────
  function isModuleLocked(index) {
    if (!course?.enableModuleLocking) return false;
    if (index === 0) return false;
    const prevMod = modules[index - 1];
    if (!prevMod) return false;
    const prev = progressMap[prevMod.moduleId] ?? {};
    return !(
      prev.status === "completed" ||
      prev.status === "passed" ||
      (prev.percentage ?? 0) >= 100
    );
  }

  // ── SCORM progress callback ───────────────────────────────────────────────
  //
  // Called twice per SCORM commit:
  //   sv = true  → fired immediately when LMSCommit() / LMSFinish() is called
  //                (one per slide navigation in Articulate 360)
  //   sv = false → fired after UPDATE_COURSE_PROGRESS finishes
  //
  // Progress-API strategy:
  //   • sv=true  : call saveOverallProgress immediately on every slide change
  //   • sv=false : update the sidebar state; also re-call saveOverallProgress
  //                when the module is marked completed to lock in the final
  //                coursePercentage after the per-module save is confirmed.
  const handleProgressChange = useCallback((p, s, sv) => {
    setCurrentProgress(p);
    setCurrentStatus(s);
    setSaving(sv);

    const mod = modules.find((m) => m.slug === activeSlug);
    if (!mod) return;

    const currentIndex = modules.findIndex((m2) => m2.slug === activeSlug);
    const updatedMap = { ...progressMapRef.current, [mod.moduleId]: { percentage: p, status: s } };

    if (sv) {
      // ── Slide changed: fire immediately, don't wait for the per-module save ──
      saveOverallProgress(updatedMap, currentIndex);
    } else {
      // ── Per-module save confirmed: refresh the sidebar ────────────────────
      setProgressMap(updatedMap);

      // ── Module completed: re-confirm progress so the backend reflects the
      //    completed percentage even if the sv=true call raced ahead ──────────
      const isModuleComplete = s === "completed" || s === "passed" || p >= 100;
      if (isModuleComplete) {
        saveOverallProgress(updatedMap, currentIndex);
      }
    }
  }, [activeSlug, modules, saveOverallProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Slide navigation callback (fires on every lesson_location change) ───────
  // Captures slide events that arrive via LMSSetValue but WITHOUT an LMSCommit,
  // which is the normal Articulate 360 pattern for mid-session slide navigations.
  const handleSlideChange = useCallback((location) => {
    const mod = modules.find((m) => m.slug === activeSlug);
    if (!mod) return;
    const currentIndex = modules.findIndex((m2) => m2.slug === activeSlug);
    // ── Req 2: log every time a new slide of the current module starts ───────
    saveOverallProgress(progressMapRef.current, currentIndex);
  }, [activeSlug, modules, saveOverallProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Module select ─────────────────────────────────────────────────────────
  const handleModuleSelect = (slug) => {
    if (slug === activeSlug) return;
    const index = modules.findIndex((m) => m.slug === slug);
    if (isModuleLocked(index)) return;
    setShowNextConfirm(false);
    setActionError("");
    setActiveSlug(slug);
    saveOverallProgress(progressMapRef.current, index);
  };

  // ── Returns true if a saved progress entry counts as completed ─────────────
  const isEntryComplete = (entry) =>
    !!entry &&
    (entry.status === "completed" ||
      entry.status === "passed" ||
      (entry.percentage ?? 0) >= 100);

  // ── Mark the active module complete ────────────────────────────────────────
  //   coursePercentage = currentCoursePercentage + 20 (each module is worth 20%).
  const handleMarkComplete = async () => {
    const mod = modules.find((m) => m.slug === activeSlug);
    if (!mod) return;
    const index = modules.findIndex((m) => m.slug === activeSlug);
    setMarkingComplete(true);
    setActionError("");

    // Overall % from modules already complete, excluding the active one.
    const priorCompleted = modules.filter(
      (m) => m.slug !== mod.slug && isEntryComplete(progressMapRef.current[m.moduleId])
    ).length;
    const newPercentage = Math.min(100, priorCompleted * 20 + 20);

    try {
      await axiosService.post(ApiRoutes.COURSE_PROGRESS, {
        courseId: id,
        coursePercentage: newPercentage,
        currentPage: String(index + 1),
        start_time: null,
        end_time: new Date().toISOString(),
        module_name: mod.title,
      });
      const updated = {
        ...progressMapRef.current,
        [mod.moduleId]: { percentage: 100, status: "completed" },
      };
      setProgressMap(updated);
      setCurrentProgress(100);
      setCurrentStatus("completed");
    } catch {
      setActionError("Failed to mark the module as completed. Please try again.");
    } finally {
      setMarkingComplete(false);
    }
  };

  // ── Confirm + start the next module ────────────────────────────────────────
  const handleStartNext = async () => {
    const index = modules.findIndex((m) => m.slug === activeSlug);
    const nextMod = modules[index + 1];
    if (!nextMod) return;
    setStartingNext(true);
    setActionError("");

    const completedNow = modules.filter((m) =>
      isEntryComplete(progressMapRef.current[m.moduleId])
    ).length;
    const overall = Math.min(100, completedNow * 20);

    try {
      await axiosService.post(ApiRoutes.COURSE_PROGRESS, {
        courseId: id,
        coursePercentage: overall,
        currentPage: String(index + 2),
        start_time: new Date().toISOString(),
        end_time: null,
        module_name: nextMod.title,
      });
      setShowNextConfirm(false);
      setActiveSlug(nextMod.slug);
      saveOverallProgress(progressMapRef.current, index + 1);
    } catch {
      setActionError("Failed to start the next module. Please try again.");
    } finally {
      setStartingNext(false);
    }
  };

  if (!course) {
    return (
      <div className="course-dashboard-section">
        <p className="text-danger">Course not found.</p>
      </div>
    );
  }

  const activeMod = modules.find((m) => m.slug === activeSlug);
  const activeIndex = modules.findIndex((m) => m.slug === activeSlug);
  const isActiveComplete = isEntryComplete(progressMap[activeMod?.moduleId]);

  return (
    <>
      <ListingHeader
        title={course.title}
        breadCrumb={[
          { label: "Dashboard", href: "/dashboard" },
          { label: course.title, href: `/course/${id}` },
        ]}
        actions={[]}
        dropdown={null}
        rightSlot={
          <ProgressRing
            progress={currentProgress}
            status={currentStatus}
            saving={saving}
          />
        }
      />

      {loading ? (
        <div className="course-loader-wrapper">
          <Loader text="Loading course…" />
        </div>
      ) : (
        <div className="course-split">
          {/* ── Sidebar ── */}
          <div className="course-split__sidebar">
            <div className="course-split__sidebar-header">
              <p className="course-split__sidebar-title">
                {modules.length} Modules
              </p>
            </div>

            {modules.map((mod, index) => {
              const saved = progressMap[mod.moduleId] ?? {};
              const pct = Math.min(100, saved.percentage ?? 0);
              const isDone =
                saved.status === "completed" ||
                saved.status === "passed" ||
                pct >= 100;
              const isActive = mod.slug === activeSlug;
              const locked = isModuleLocked(index);

              return (
                <div key={mod.slug}>
                  <button
                    className={[
                      "module-sidebar-item",
                      isActive ? "module-sidebar-item--active" : "",
                      isDone   ? "module-sidebar-item--done"   : "",
                      locked   ? "module-sidebar-item--locked" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => handleModuleSelect(mod.slug)}
                    disabled={locked}
                    aria-disabled={locked}
                  >
                    <div className="module-sidebar-item__badge">
                      {locked
                        ? <Lock size={13} />
                        : isDone
                          ? <CheckCircle size={14} />
                          : String(index + 1).padStart(2, "0")}
                    </div>
                    <div className="module-sidebar-item__content">
                      <div className="module-sidebar-item__title">{mod.title}</div>
                      <div className="module-sidebar-item__meta">{mod.duration} · {mod.slides} slides</div>
                      <div className="module-sidebar-item__bar-track">
                        <div
                          className="module-sidebar-item__bar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </button>

                  {/* ── Completion / next-module actions for the active module ── */}
                  {isActive && (
                    <div
                      className="module-sidebar-item__actions"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        padding: "10px 12px 14px",
                      }}
                    >
                      {!isActiveComplete ? (
                        <button
                          type="button"
                          className="btn btn-rescuern-primary"
                          onClick={handleMarkComplete}
                          disabled={markingComplete}
                        >
                          {markingComplete ? "Saving…" : "Mark as Completed"}
                        </button>
                      ) : index < modules.length - 1 ? (
                        <button
                          type="button"
                          className="btn btn-rescuern-primary"
                          onClick={() => setShowNextConfirm(true)}
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                        >
                          <PlayCircle size={16} />
                          Start Next Module
                        </button>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            color: "var(--brand-navy)",
                            fontWeight: 600,
                          }}
                        >
                          <CheckCircle size={16} />
                          Module completed
                        </span>
                      )}

                      {actionError && <span className="text-danger">{actionError}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Viewer ── */}
          <div className="course-split__viewer" style={{ position: "relative" }}>
            {activeMod && (
              <CourseViewer
                key={activeMod.moduleId}
                courseId={activeMod.moduleId}
                courseUrl={activeMod.url}
                courseTitle={activeMod.title}
                paused={isActiveComplete}
                onProgressChange={handleProgressChange}
                onSlideChange={handleSlideChange}
              />
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={showNextConfirm}
        title="Start next module?"
        onClose={() => !startingNext && setShowNextConfirm(false)}
        onConfirm={handleStartNext}
        confirmLabel={startingNext ? "Starting…" : "Yes, start"}
        confirmDisabled={startingNext}
      >
        <p style={{ margin: 0 }}>
          Ready to start{" "}
          <strong>{modules[activeIndex + 1]?.title}</strong>?
        </p>
      </Modal>

      {/* ── Course-complete celebration ── */}
      {showCongrats && (
        <div className="course-congrats-overlay" role="dialog" aria-modal="true">
          <div className="course-congrats-confetti" aria-hidden="true">
            {CONFETTI_PIECES.map((c, i) => (
              <span
                key={i}
                className="confetti-piece"
                style={{
                  left: `${c.left}%`,
                  width: c.width,
                  height: c.height,
                  background: c.color,
                  animationDelay: `${c.delay}s`,
                  animationDuration: `${c.duration}s`,
                  "--drift": `${c.drift}px`,
                  "--rotate": `${c.rotate}deg`,
                }}
              />
            ))}
          </div>

          <div className="course-congrats-card">
            <div className="course-congrats-badge" aria-hidden="true">
              <CheckCircle size={44} strokeWidth={2.5} />
            </div>

            <h2 className="course-congrats-title">Congratulations! 🎉</h2>
            <p className="course-congrats-text">You’ve successfully completed</p>
            <p className="course-congrats-course">{course.title}</p>

            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <button
              type="button"
              className="btn btn-rescuern-primary course-congrats-btn"
              onClick={() => navigate("/dashboard")}
            >
              Go To Dashboard
            </button>
            </div>
            

            <p className="course-congrats-timer">
              Redirecting to dashboard in <strong>{countdown}s</strong>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
